import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';
import { useNotifications } from '../../contexts/NotificationContext';
import StatCard from './StatCard';

const ErrorsTab = ({ token }) => {
  const { showSuccess } = useNotifications();
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    errorType: '',
    severity: '',
    resolved: ''
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedError, setSelectedError] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolving, setResolving] = useState(false);

  // Charger les erreurs
  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...(filters.errorType && { errorType: filters.errorType }),
        ...(filters.severity && { severity: filters.severity }),
        ...(filters.resolved && { resolved: filters.resolved })
      });

      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/api/errors?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const result = await response.json();
      if (result.success) {
        setErrors(result.data.errors);
        setTotalPages(result.data.totalPages);
      }
    } catch (err) {
      console.error('Erreur chargement errors:', err);
    } finally {
      setLoading(false);
    }
  }, [token, page, filters]);

  useEffect(() => {
    fetchErrors();
  }, [fetchErrors]);

  // Charger les stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/api/errors/stats`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const result = await response.json();
        if (result.success) {
          setStats(result.data);
        }
      } catch (err) {
        console.error('Erreur chargement stats:', err);
      }
    };
    fetchStats();
  }, [token]);

  const handleResolve = async (id) => {
    setResolving(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/errors/${id}/resolve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: resolveNotes })
      });
      const result = await response.json();
      if (result.success) {
        setSelectedError(null);
        setResolveNotes('');
        fetchErrors();
      }
    } catch (err) {
      console.error('Erreur resolution:', err);
    } finally {
      setResolving(false);
    }
  };

  const handleUnresolve = async (id) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/errors/${id}/unresolve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setSelectedError(null);
        fetchErrors();
      }
    } catch (err) {
      console.error('Erreur reouverture:', err);
    }
  };

  const handleExport = async (id, format) => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/api/errors/${id}/export/${format}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `error_${id}.${format === 'markdown' ? 'md' : 'json'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur export:', err);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: '#4caf50',
      medium: '#ff9800',
      high: '#f44336',
      critical: '#9c27b0'
    };
    return colors[severity] || '#666';
  };

  const getSeverityLabel = (severity) => {
    const labels = {
      low: 'Basse',
      medium: 'Moyenne',
      high: 'Haute',
      critical: 'Critique'
    };
    return labels[severity] || severity;
  };

  const getTypeIcon = (type) => {
    const icons = { api: '[API]', client: '[JS]', react: '[React]', service: '[SVC]' };
    return icons[type] || '[?]';
  };

  const getTypeLabel = (type) => {
    const labels = { api: 'API', client: 'JavaScript', react: 'React', service: 'Service' };
    return labels[type] || type;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      {/* Stats */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <StatCard icon="[!]" title="Non resolues" value={stats.unresolved} color="#f44336" />
          <StatCard icon="[!!]" title="Critiques" value={stats.critical_unresolved} color="#9c27b0" />
          <StatCard icon="[API]" title="Erreurs API" value={stats.api_errors} color="#2196f3" />
          <StatCard icon="[SVC]" title="Erreurs Service" value={stats.service_errors || 0} color="#673ab7" />
          <StatCard icon="[JS]" title="Erreurs JS" value={stats.client_errors} color="#ff9800" />
          <StatCard icon="[R]" title="Erreurs React" value={stats.react_errors} color="#00bcd4" />
          <StatCard icon="[T]" title="Total" value={stats.total} color="#666" />
        </div>
      )}

      {/* Filtres */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        padding: '16px',
        background: '#f5f5f5',
        borderRadius: '12px'
      }}>
        <select
          value={filters.errorType}
          onChange={(e) => { setFilters(f => ({ ...f, errorType: e.target.value })); setPage(1); }}
          style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px', background: 'white' }}
        >
          <option value="">Tous types</option>
          <option value="api">API</option>
          <option value="service">Service (Backend)</option>
          <option value="client">JavaScript</option>
          <option value="react">React</option>
        </select>
        <select
          value={filters.severity}
          onChange={(e) => { setFilters(f => ({ ...f, severity: e.target.value })); setPage(1); }}
          style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px', background: 'white' }}
        >
          <option value="">Toutes severites</option>
          <option value="critical">Critique</option>
          <option value="high">Haute</option>
          <option value="medium">Moyenne</option>
          <option value="low">Basse</option>
        </select>
        <select
          value={filters.resolved}
          onChange={(e) => { setFilters(f => ({ ...f, resolved: e.target.value })); setPage(1); }}
          style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px', background: 'white' }}
        >
          <option value="">Tous statuts</option>
          <option value="false">Non resolues</option>
          <option value="true">Resolues</option>
        </select>
        <button
          onClick={() => { setFilters({ errorType: '', severity: '', resolved: '' }); setPage(1); }}
          style={{
            padding: '8px 16px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            background: 'white',
            cursor: 'pointer'
          }}
        >
          Reinitialiser
        </button>
      </div>

      {/* Liste des erreurs */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>Chargement...</div>
        ) : errors.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            Aucune erreur trouvee avec ces filtres
          </div>
        ) : (
          errors.map((err, idx) => (
            <div
              key={err.id}
              style={{
                padding: '16px 20px',
                borderBottom: idx < errors.length - 1 ? '1px solid #eee' : 'none',
                cursor: 'pointer',
                background: err.resolved ? '#fafafa' : 'white',
                transition: 'background 0.2s'
              }}
              onClick={() => setSelectedError(err)}
              onMouseOver={(e) => e.currentTarget.style.background = '#f5f5f5'}
              onMouseOut={(e) => e.currentTarget.style.background = err.resolved ? '#fafafa' : 'white'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  fontFamily: 'monospace',
                  color: '#666',
                  fontSize: '12px',
                  minWidth: '60px'
                }}>
                  {getTypeIcon(err.error_type)}
                </span>
                <span style={{
                  padding: '3px 10px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: 'white',
                  background: getSeverityColor(err.severity),
                  minWidth: '70px',
                  textAlign: 'center'
                }}>
                  {getSeverityLabel(err.severity)}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: '500',
                    textDecoration: err.resolved ? 'line-through' : 'none',
                    color: err.resolved ? '#999' : '#333',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {err.message}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {formatDate(err.created_at)}
                    {err.user_name && ` - ${err.user_name}`}
                    {err.context?.route && ` - ${err.context.method} ${err.context.route}`}
                  </div>
                </div>
                {err.resolved && (
                  <span style={{
                    color: '#4caf50',
                    fontSize: '12px',
                    fontWeight: '500',
                    padding: '4px 8px',
                    background: '#e8f5e9',
                    borderRadius: '4px'
                  }}>Resolu</span>
                )}
              </div>
            </div>
          ))
        )}

        {/* Pagination */}
        {!loading && errors.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            padding: '16px',
            borderTop: '1px solid #eee'
          }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
                opacity: page === 1 ? 0.5 : 1,
                background: 'white'
              }}
            >
              Precedent
            </button>
            <span style={{ padding: '8px 16px', color: '#666' }}>
              Page {page} / {totalPages || 1}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                opacity: page >= totalPages ? 0.5 : 1,
                background: 'white'
              }}
            >
              Suivant
            </button>
          </div>
        )}
      </div>

      {/* Modale detail */}
      {selectedError && (
        <ErrorDetailModal
          error={selectedError}
          onClose={() => { setSelectedError(null); setResolveNotes(''); }}
          onResolve={handleResolve}
          onUnresolve={handleUnresolve}
          onExport={handleExport}
          onCopyJson={() => {
            navigator.clipboard.writeText(JSON.stringify(selectedError, null, 2));
            showSuccess('JSON copié dans le presse-papier');
          }}
          resolveNotes={resolveNotes}
          setResolveNotes={setResolveNotes}
          resolving={resolving}
          getSeverityColor={getSeverityColor}
          getSeverityLabel={getSeverityLabel}
          getTypeLabel={getTypeLabel}
          formatDate={formatDate}
        />
      )}
    </div>
  );
};

const ErrorDetailModal = ({
  error,
  onClose,
  onResolve,
  onUnresolve,
  onExport,
  onCopyJson,
  resolveNotes,
  setResolveNotes,
  resolving,
  getSeverityColor,
  getSeverityLabel,
  getTypeLabel,
  formatDate
}) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '900px',
          width: '90%',
          maxHeight: '85vh',
          overflow: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header avec actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <div>
            <h3 style={{ margin: '0 0 8px 0' }}>Erreur #{error.id}</h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                color: 'white',
                background: getSeverityColor(error.severity)
              }}>
                {getSeverityLabel(error.severity)}
              </span>
              <span style={{
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                background: '#e3f2fd',
                color: '#1976d2'
              }}>
                {getTypeLabel(error.error_type)}
              </span>
              {error.resolved && (
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  background: '#e8f5e9',
                  color: '#388e3c'
                }}>
                  Resolu
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => onExport(error.id, 'markdown')}
              style={{
                padding: '8px 12px',
                background: '#9c27b0',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              Export Claude (MD)
            </button>
            <button
              onClick={() => onExport(error.id, 'json')}
              style={{
                padding: '8px 12px',
                background: '#2196f3',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              Export JSON
            </button>
            <button
              onClick={onCopyJson}
              style={{
                padding: '8px 12px',
                background: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              Copier JSON
            </button>
            {!error.resolved && (
              <button
                onClick={() => onResolve(error.id)}
                disabled={resolving}
                style={{
                  padding: '8px 12px',
                  background: resolving ? '#ccc' : '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: resolving ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                {resolving ? '...' : 'Résoudre'}
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '8px 12px',
                background: '#f5f5f5',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              X
            </button>
          </div>
        </div>

        {/* Zone resolution rapide (en haut) */}
        {!error.resolved && (
          <div style={{
            marginBottom: '20px',
            padding: '12px 16px',
            background: '#fff8e1',
            borderRadius: '8px',
            border: '1px solid #ffe082'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={resolveNotes}
                onChange={(e) => setResolveNotes(e.target.value)}
                placeholder="Notes de résolution (optionnel)"
                style={{
                  flex: 1,
                  minWidth: '200px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontSize: '13px'
                }}
              />
              <button
                onClick={() => onResolve(error.id)}
                disabled={resolving}
                style={{
                  padding: '8px 16px',
                  background: resolving ? '#ccc' : '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: resolving ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap'
                }}
              >
                {resolving ? 'En cours...' : 'Marquer comme résolu'}
              </button>
            </div>
          </div>
        )}

        {/* Info utilisateur et date */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '20px',
          padding: '16px',
          background: '#f5f5f5',
          borderRadius: '8px'
        }}>
          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Date</div>
            <div style={{ fontWeight: '500' }}>{formatDate(error.created_at)}</div>
          </div>
          {error.user_name && (
            <div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Utilisateur</div>
              <div style={{ fontWeight: '500' }}>{error.user_name}</div>
              {error.user_email && (
                <div style={{ fontSize: '12px', color: '#666' }}>{error.user_email}</div>
              )}
            </div>
          )}
          {error.context?.route && (
            <div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Route</div>
              <div style={{ fontWeight: '500', fontFamily: 'monospace' }}>
                {error.context.method} {error.context.route}
              </div>
            </div>
          )}
          {error.context?.status_code && (
            <div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Code HTTP</div>
              <div style={{ fontWeight: '500' }}>{error.context.status_code}</div>
            </div>
          )}
        </div>

        {/* Message */}
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>Message</h4>
          <pre style={{
            background: '#f5f5f5',
            padding: '12px',
            borderRadius: '8px',
            overflow: 'auto',
            margin: 0,
            fontSize: '13px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {error.message}
          </pre>
        </div>

        {/* Stack trace */}
        {error.stack_trace && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>Stack Trace</h4>
            <pre style={{
              background: '#263238',
              color: '#80cbc4',
              padding: '12px',
              borderRadius: '8px',
              fontSize: '11px',
              overflow: 'auto',
              maxHeight: '200px',
              margin: 0
            }}>
              {error.stack_trace}
            </pre>
          </div>
        )}

        {/* Contexte */}
        {error.context && Object.keys(error.context).length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>Contexte</h4>
            <pre style={{
              background: '#f5f5f5',
              padding: '12px',
              borderRadius: '8px',
              fontSize: '12px',
              overflow: 'auto',
              maxHeight: '300px',
              margin: 0
            }}>
              {JSON.stringify(error.context, null, 2)}
            </pre>
          </div>
        )}

        {/* Info resolution */}
        {error.resolved && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            background: '#e8f5e9',
            borderRadius: '8px'
          }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#388e3c' }}>Resolution</h4>
            <div style={{ fontSize: '13px' }}>
              <div><strong>Resolu par :</strong> {error.resolved_by_name || 'N/A'}</div>
              <div><strong>Date :</strong> {formatDate(error.resolved_at)}</div>
              {error.resolution_notes && (
                <div style={{ marginTop: '8px' }}>
                  <strong>Notes :</strong>
                  <p style={{ margin: '4px 0 0 0' }}>{error.resolution_notes}</p>
                </div>
              )}
            </div>
            <button
              onClick={() => onUnresolve(error.id)}
              style={{
                marginTop: '12px',
                padding: '8px 16px',
                background: '#ff9800',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Rouvrir cette erreur
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default ErrorsTab;
